import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  ThemeProvider,
  Tooltip
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { jaJP } from "@mui/x-data-grid/locales";
import { ReactComponent as ToolEdit } from "assets/icons/estate_info/tool_edit.svg";
import { ReactComponent as ToolLink } from "assets/icons/estate_info/tool_link.svg";
import { ReactComponent as ToolPrint } from "assets/icons/estate_info/tool_print.svg";
import { ReactComponent as ToolReport } from "assets/icons/estate_info/tool_report.svg";
import { ReactComponent as ToolTrash } from "assets/icons/estate_info/tool_trash.svg";
import { StatusNameEnum } from "common/enums/StatusEnum";
import { calcOld } from "common/functions/estates";
import { isAllowedWebUrl } from "common/functions/security";
import { MinimalEstate } from "common/queries/minimalEstates";
import { isEmpty } from "lodash";
import React from "react";
import { EstateItemIcon } from "../EstateItemIcon";
import styles from "./EstatesList.module.scss";
import { useEstateListHooks } from "./hooks";
import { EstatesListProps } from "./interfaces";

export const EstatesList = React.memo((props: EstatesListProps) => {
  const {
    toolTipTheme,
    dataGridTheme,
    progressTheme,
    selectedEstatesForPrint,
    isAdministrator,
    isDomain,
    onDisplayEstate,
    onSelectEstateForPrint,
    onClearSelectedEstatesForPrint,
    onPrintSelectedEstates
  } = useEstateListHooks(props);

  const { incrementSiteAtomicCounter } = props;

  const columns: GridColDef<MinimalEstate>[] = [
    {
      field: "id",
      headerName: "ID",
      width: 244,
      renderCell: (params) => (
        <Box className={styles.idWrapper}>
          <EstateItemIcon estate={params.row} />
          <Box className={styles.id}>{params.value}</Box>
          <ThemeProvider theme={toolTipTheme}>
            <Tooltip title="報告" placement="top" followCursor>
              <Box component="span">
                <IconButton
                  className={styles.operationButton}
                  disabled={
                    props.disabled ||
                    !(
                      isAdministrator ||
                      isDomain(params.row as MinimalEstate) ||
                      (params.row as MinimalEstate).status !==
                        StatusNameEnum.成約済み
                    )
                  }
                  onClick={() => {
                    props.onEstateClick(params.row as MinimalEstate, "report");
                  }}
                >
                  <Box
                    className={
                      props.disabled ||
                      !(
                        isAdministrator ||
                        isDomain(params.row as MinimalEstate) ||
                        (params.row as MinimalEstate).status !==
                          StatusNameEnum.成約済み
                      )
                        ? styles.disabled
                        : styles.reportable
                    }
                  >
                    <ToolReport />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title="編集" placement="top" followCursor>
              <Box component="span">
                <IconButton
                  className={styles.operationButton}
                  disabled={
                    props.disabled ||
                    (!isAdministrator && !isDomain(params.row as MinimalEstate))
                  }
                  onClick={() => {
                    props.onEstateClick(params.row as MinimalEstate, "update");
                  }}
                >
                  <Box
                    className={
                      props.disabled ||
                      (!isAdministrator &&
                        !isDomain(params.row as MinimalEstate))
                        ? styles.disabled
                        : styles.editable
                    }
                  >
                    <ToolEdit />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title="削除" placement="top" followCursor>
              <Box component="span">
                <IconButton
                  className={styles.operationButton}
                  disabled={
                    props.disabled ||
                    (!isAdministrator && !isDomain(params.row as MinimalEstate))
                  }
                  onClick={() => {
                    props.onEstateClick(params.row as MinimalEstate, "delete");
                  }}
                >
                  <Box
                    className={
                      props.disabled ||
                      (!isAdministrator &&
                        !isDomain(params.row as MinimalEstate))
                        ? styles.disabled
                        : styles.deletable
                    }
                  >
                    <ToolTrash />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
            <Tooltip title="詳細" placement="top" followCursor>
              <Box component="span">
                {(() => {
                  const estateUrl = (params.row as MinimalEstate).url ?? "";
                  const linkEnabled =
                    !isEmpty(estateUrl) && isAllowedWebUrl(estateUrl);

                  return (
                    <IconButton
                      className={styles.operationButton}
                      href={linkEnabled ? estateUrl : undefined}
                      disabled={!linkEnabled}
                      target="_blank"
                      rel="noopener noreferrer"
                      component={linkEnabled ? "a" : "button"}
                      onClick={() => {
                        if (linkEnabled) {
                          incrementSiteAtomicCounter(estateUrl);
                        }
                      }}
                    >
                      <Box
                        className={
                          isEmpty(
                            (params.row as MinimalEstate).estateUrl ??
                              (params.row as MinimalEstate).url
                          ) || !linkEnabled
                            ? styles.disabled
                            : styles.referable
                        }
                      >
                        <ToolLink />
                      </Box>
                    </IconButton>
                  );
                })()}
              </Box>
            </Tooltip>
            <Tooltip title="印刷" placement="top" followCursor>
              <Box component="span">
                <IconButton
                  className={`print-button ${styles.operationButton}`}
                  disabled={
                    props.disabled ||
                    !(
                      isAdministrator ||
                      isDomain(params.row as MinimalEstate) ||
                      (params.row as MinimalEstate).status !==
                        StatusNameEnum.成約済み
                    )
                  }
                  onClick={() => onDisplayEstate(params.row as MinimalEstate)}
                >
                  <Box
                    className={
                      props.disabled ||
                      !(
                        isAdministrator ||
                        isDomain(params.row as MinimalEstate) ||
                        (params.row as MinimalEstate).status !==
                          StatusNameEnum.成約済み
                      )
                        ? styles.disabled
                        : styles.printable
                    }
                  >
                    <ToolPrint />
                  </Box>
                </IconButton>
              </Box>
            </Tooltip>
          </ThemeProvider>
        </Box>
      )
    },
    {
      field: "select",
      headerName: "複数印刷",
      headerAlign: "center",
      width: 100,
      renderCell: (params) => (
        <Box className={styles.selectWrapper}>
          <Checkbox
            className={`${styles.selectCheckbox} ${
              (selectedEstatesForPrint.length >= 10 &&
                !selectedEstatesForPrint.includes(
                  (params.row as MinimalEstate).id ?? ""
                )) ||
              !(
                isAdministrator ||
                isDomain(params.row as MinimalEstate) ||
                (params.row as MinimalEstate).status !== StatusNameEnum.成約済み
              )
                ? styles.disabled
                : ""
            }`}
            size="small"
            disabled={
              (selectedEstatesForPrint.length >= 10 &&
                !selectedEstatesForPrint.includes(
                  (params.row as MinimalEstate).id ?? ""
                )) ||
              !(
                isAdministrator ||
                isDomain(params.row as MinimalEstate) ||
                (params.row as MinimalEstate).status !== StatusNameEnum.成約済み
              )
            }
            checked={selectedEstatesForPrint.includes(
              (params.row as MinimalEstate).id ?? ""
            )}
            onChange={(_, checked) =>
              onSelectEstateForPrint(params.row as MinimalEstate, checked)
            }
          />
        </Box>
      )
    },
    {
      field: "status",
      headerName: "ステータス",
      width: 100,
      renderCell: (params) =>
        params.value === StatusNameEnum.下書き
          ? StatusNameEnum.位置不明
          : params.value
    },
    {
      field: "createdAt",
      headerName: "登録日",
      width: 110,
      renderCell: (params) =>
        params.value
          ? new Date(params.value).toLocaleDateString("ja-JP", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit"
            })
          : ""
    },
    {
      field: "updatedAt",
      headerName: "掲載終了",
      width: 110
    },
    {
      field: "livingDays",
      headerName: "売出日数",
      width: 100,
      type: "number",
      align: "right"
    },
    {
      field: "companyName",
      headerName: "業者名",
      width: 200
    },
    { field: "mediationKind", headerName: "媒介種別", width: 100 },
    { field: "estateType", headerName: "物件種別", width: 100 },
    { field: "condominiumName", headerName: "マンション名", width: 200 },
    { field: "currentStatus", headerName: "現況", width: 140 },
    { field: "bldConditionType", headerName: "建築条件", width: 100 },
    {
      field: "estatePrice",
      headerName: "価格(万円)",
      width: 100,
      type: "number",
      align: "right"
    },
    {
      field: "tsuboArea",
      headerName: "面積(坪)",
      width: 100,
      type: "number",
      align: "right"
    },
    {
      field: "unitPrice",
      headerName: "坪単価(万円)",
      width: 100,
      type: "number",
      align: "right"
    },
    { field: "layout", headerName: "間取り", width: 100 },
    {
      field: "completionMonth",
      headerName: "築年数",
      width: 100,
      type: "number",
      valueGetter: (_, row) => calcOld(row as MinimalEstate),
      align: "right",
      renderCell: (params) => params.value ?? ""
    },
    { field: "prefecture", headerName: "都道府県", width: 100 },
    { field: "city", headerName: "市区町村", width: 100 },
    { field: "area", headerName: "町域", width: 120 }
  ];

  const localeText = {
    ...jaJP.components.MuiDataGrid.defaultProps.localeText,
    "filterOperator!=": "...と等しくない",
    "filterOperator<=": "...以下",
    "filterOperator>=": "...以上",
    "filterOperator<": "...より小さい",
    "filterOperator>": "...より大きい",
    "filterOperator=": "...に等しい"
  };

  return (
    <Box id="estate-list" className={styles.estatesList}>
      {props.busy ? (
        <Box className={styles.progress}>
          <ThemeProvider theme={progressTheme}>
            <CircularProgress color={"primary"} />
          </ThemeProvider>
        </Box>
      ) : (
        <>
          <ThemeProvider theme={dataGridTheme}>
            <DataGrid
              rows={props.estates}
              columns={columns}
              localeText={localeText}
              density="compact"
              className={styles.dataGrid}
            />
          </ThemeProvider>
          <Box className={styles.multiPrint}>
            <Box
              className={`${styles.button} ${styles.clear} ${
                selectedEstatesForPrint.length > 0 ? "" : styles.disabled
              }`}
              onClick={onClearSelectedEstatesForPrint}
            >
              選択を全解除
            </Box>
            <Box
              className={`${styles.button} ${
                selectedEstatesForPrint.length > 0 ? "" : styles.disabled
              }`}
              onClick={onPrintSelectedEstates}
            >
              選択した物件を印刷
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
});
